import { runInAction } from 'mobx';
import { Task } from 'redux-saga';
import { take, fork, cancelled } from 'redux-saga/effects';
import { Channel } from '../../../Shared/utils/user-action-channel';
import { callInAction, ActionHandlers, AsyncActionHandler, ActionConcurrencyInstruction, Action } from '../../../Shared/utils/saga-utils';
import { runSaga } from '../../utils/saga-utils';
import { isFunction } from '../../utils/is-function';
import {
	getScheduleDetailsDataService, ScheduleDetailsActionRequest, ScheduleDetailsActionResponse,
	ScheduleDetailsApiConfigActions
} from '../schedule-details-data-service';
import { ScheduleImportApiConfig } from '../schedule-import-generated';
import { reportScheduleImportError } from '../helpers/schedule-import-error-utils';

export interface IScheduleImportSagaContext {
	readonly userActionChannel: Channel<any>;
	readonly mainViewModel: any;
	readonly dataService: ScheduleImportSagaDataService;
	executingBlockingAction?: boolean;
}

function getUserActionName<TActionType extends Action>(userAction: TActionType): string | undefined {
	return (userAction as any).constructor.name;
}

export function runScheduleImportSaga<TActionType extends Action>(
	sagaContext: IScheduleImportSagaContext,
	actionHandlers: ActionHandlers<IScheduleImportSagaContext, TActionType>,
	onSagaInit?: (context: IScheduleImportSagaContext) => void) {
	runSaga({}, scheduleImportSaga, sagaContext, actionHandlers, onSagaInit);
}

function* scheduleImportSaga<TActionType extends Action>(context: IScheduleImportSagaContext,
														actionHandlers: ActionHandlers<IScheduleImportSagaContext, TActionType>, onSagaInit?: (context: IScheduleImportSagaContext) => void): IterableIterator<any> {

	const { userActionChannel } = context;

	let currentAsyncHandler: AsyncActionHandler<IScheduleImportSagaContext> | undefined;
	let currentAsyncTask: Task | undefined;

	if (isFunction(onSagaInit)) {
		yield onSagaInit(context);
	}

	while (true) {
		const userAction: TActionType = yield take(userActionChannel);
		try {
			const incomingActionHandler = actionHandlers.get(userAction);
			console.log('userAction', getUserActionName(userAction));

			if (currentAsyncHandler && currentAsyncTask && currentAsyncTask.isRunning()) {
				const instruction = actionHandlers.getInstructionForIncomingAction(currentAsyncHandler, incomingActionHandler);

				switch (instruction) {
					case ActionConcurrencyInstruction.AllowIncoming:
						console.log('Allowing incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						break;
					case ActionConcurrencyInstruction.CancelRunning:
						console.log('Cancelling running action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						if (currentAsyncHandler.blocking) {
							context.executingBlockingAction = false;
						}
						currentAsyncTask.cancel();
						currentAsyncHandler = undefined;
						break;
					case ActionConcurrencyInstruction.BlockIncoming:
						console.log('Blocking incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						continue;
					default:
						throw new Error(`ActionConcurrencyInstruction[${instruction}] is not supported`);
				}
			}

			console.log('Starting action', { incoming: incomingActionHandler.actionType.name });
			if (incomingActionHandler.async) {
				currentAsyncHandler = incomingActionHandler;

				if (currentAsyncHandler.blocking) {
					context.executingBlockingAction = true;
				}

				currentAsyncTask = yield fork(function* () {
					try {
						yield callInAction(incomingActionHandler.handler, context, userAction);
					} catch (ex) {
						reportScheduleImportError(ex, {
							sagaRunningFork: true,
							sagaUserAction: getUserActionName(userAction),
						});
					} finally {
						if (!(yield cancelled())) {
							context.executingBlockingAction = false;
						}
					}
				});
			} else {
				runInAction(() => incomingActionHandler.handler(context, userAction));
			}
		} catch (ex) {
			reportScheduleImportError(ex, {
				sagaUserAction: getUserActionName(userAction),
			});
		}
	}
}

export type ScheduleImportSagaDataService = {
	[x in keyof ScheduleDetailsApiConfigActions]: (request:ScheduleDetailsActionRequest<x>) => Iterator<ScheduleDetailsActionResponse<x>>
};

export function sagaDataService(): ScheduleImportSagaDataService {
	return (Object.keys(ScheduleImportApiConfig.actions) as Array<keyof ScheduleDetailsApiConfigActions>).reduce((acc: any, actionKey: keyof ScheduleDetailsApiConfigActions) => {
		acc[actionKey] = function* (request: any) {
			const action: (...args: any[]) => PromiseLike<any> = getScheduleDetailsDataService()[actionKey];
			return yield action(request);
		};

		return acc;
	}, {});
}
